// growthBook.js
'use strict';
import { GrowthBook } from '@growthbook/growthbook';

import { remoteConfig as remoteConfigDebug } from '../resource/debug.js';
import { EMPTY_OBJECT } from '../resource/defaults.js';
import env from '../resource/env.js';
import { GROWTHBOOK_DEFAULT_TIMEOUT_MSEC } from '../resource/configConstants.js';

const growthBookLog = remoteConfigDebug.extend('log:growthBook');
const growthBookError = remoteConfigDebug.extend('error:growthBook');

let initResolvers;
let refreshResolvers;
let growthBookInstance;

/**
 * Initialize the GrowthBook SDK instance.
 *
 * Creates a singleton GrowthBook instance and resolves once the SDK has
 * fetched its features. Subsequent calls reuse the in-flight or completed
 * promise (the instance is initialized once per page load).
 *
 * Callers that need to apply targeting attributes must call `setAttribute`
 * after `init` resolves; `init` itself never sets attributes.
 *
 * @param {object} [options]
 * @param {number} [options.timeout=1000] - Timeout in ms for the SDK init.
 * @param {boolean} [options.streaming=false] - Enable streaming updates.
 * @returns {Promise<GrowthBook>} Resolves with the GrowthBook instance on
 *   success, rejects with the underlying error on failure.
 */
const init = async ({
  timeout = GROWTHBOOK_DEFAULT_TIMEOUT_MSEC,
  streaming = false,
} = {}) => {
  const log = growthBookLog.extend('init');
  const errorLog = growthBookError.extend('init');

  if (initResolvers) {
    log('reuse existing init resolvers', { timeout, now: Date.now() });
    return initResolvers.promise;
  }

  log('create new init resolvers', { timeout, now: Date.now() });

  initResolvers = Promise.withResolvers();
  const { promise } = initResolvers;

  try {
    const gb = new GrowthBook({
      apiHost: env.GROWTHBOOK_API_HOST,
      clientKey: env.GROWTHBOOK_CLIENT_KEY,
      ...(env.GROWTHBOOK_DECRYPTION_KEY && {
        decryptionKey: env.GROWTHBOOK_DECRYPTION_KEY,
      }),
    });

    log('start init growthbook', { timeout, now: Date.now() });

    const result = await gb.init({
      timeout,
      streaming,
    });

    log('done init growthbook', { timeout, result, now: Date.now() });

    if (result.success) {
      log('resolve init growthbook', { timeout, result, now: Date.now() });

      growthBookInstance = gb;
      initResolvers.resolve(growthBookInstance);
    } else {
      errorLog('reject init growthbook', { timeout, result, now: Date.now() });

      initResolvers.reject(result.error);
      initResolvers = null;
    }
  } catch (error) {
    errorLog('reject init growthbook', { timeout, error, now: Date.now() });

    initResolvers.reject(error);
    initResolvers = null;
  }

  return promise;
};

/**
 * Get all GrowthBook feature values as a plain object.
 *
 * Iterates the SDK's known features and resolves each to its evaluated
 * value (using `getFeatureValue` so the returned types reflect the values
 * configured in GrowthBook — boolean, number, string, array, object, etc).
 *
 * Returns an empty object when the SDK is not initialized or when feature
 * evaluation throws.
 *
 * @returns {Promise<Object>} Map of feature key → evaluated value.
 */
const getFeature = async () => {
  const log = growthBookLog.extend('getFeature');
  const errorLog = growthBookError.extend('getFeature');

  if (!growthBookInstance) {
    errorLog('instance not found', {
      instance: growthBookInstance,
    });

    return EMPTY_OBJECT;
  }

  log('generate features');

  try {
    const result = Object.entries(
      growthBookInstance.getFeatures() || {}
    ).reduce((accumulated, [key, item]) => {
      accumulated[key] = growthBookInstance.getFeatureValue(
        key,
        item.defaultValue
      );
      return accumulated;
    }, {});

    log('features generated', { result });

    return result;
  } catch (error) {
    errorLog('error occurred while generating features', { error });

    return EMPTY_OBJECT;
  }
};

/**
 * Refresh GrowthBook features from the remote API.
 *
 * Triggers `refreshFeatures` on the SDK to pull the latest feature
 * definitions. Concurrent calls share the same in-flight promise to
 * avoid duplicate network requests.
 *
 * Resolves with `false` (instead of rejecting) when the SDK is not
 * initialized or when the refresh throws — callers can dispatch a
 * follow-up action regardless of outcome.
 *
 * @param {object} [options]
 * @param {number} [options.timeout=1000] - Timeout in ms for the refresh.
 * @returns {Promise<boolean>} `true` on success, `false` on failure or
 *   when the instance is missing.
 */
const refresh = async ({ timeout = GROWTHBOOK_DEFAULT_TIMEOUT_MSEC } = {}) => {
  const log = growthBookLog.extend('refresh');
  const errorLog = growthBookError.extend('refresh');

  if (refreshResolvers) {
    log('reuse existing refresh resolvers');
    return refreshResolvers.promise;
  }

  refreshResolvers = Promise.withResolvers();
  const { promise, resolve } = refreshResolvers;

  if (!growthBookInstance) {
    errorLog('instance not found', {
      instance: growthBookInstance,
    });

    resolve(false);
    refreshResolvers = null;

    return promise;
  }

  log('refresh growthbook');

  try {
    await growthBookInstance.refreshFeatures({
      timeout,
    });

    log('done refresh growthbook');

    resolve(true);
  } catch (error) {
    errorLog('error occurred while refreshing growthbook', { error });

    resolve(false);
  }

  refreshResolvers = null;

  return promise;
};

/**
 * Update GrowthBook targeting attributes.
 *
 * Sets attributes on the SDK so subsequent feature evaluations target
 * the new values (e.g. when a user logs in or country changes). Callers
 * typically follow this with `refresh()` to re-evaluate features.
 *
 * No-op when the SDK is not initialized.
 *
 * @param {object} options
 * @param {object} options.attribute - Attributes to set on the SDK.
 * @returns {Promise<void>}
 */
const setAttribute = async ({ attribute }) => {
  const log = growthBookLog.extend('setAttribute');
  const errorLog = growthBookError.extend('setAttribute');

  if (!growthBookInstance) {
    errorLog('instance not found', {
      instance: growthBookInstance,
    });
    return;
  }

  log('set growthbook attribute', { attribute });

  try {
    await growthBookInstance.setAttributes(attribute);

    log('done set attribute', { attribute });
  } catch (error) {
    errorLog('error occurred while set growthbook attribute', { error });
  }
};

export { init, getFeature, refresh, setAttribute };
